import { MIXPANEL } from "./config";
import mixpanel from "mixpanel-browser";
import { hasAnalyticsConsent, onAnalyticsConsentChange } from "./helpers/analyticsConsent";
import { getSessionDurationSeconds, incrementAndGetDailyActionCount } from "./helpers/sessionActivity";

let initialized = false;

function initMixpanel() {
  if (initialized || !MIXPANEL.projectToken || !hasAnalyticsConsent()) return;

  try {
    mixpanel.init(MIXPANEL.projectToken, {
      debug: import.meta.env.DEV,
      track_pageview: true,
    });
    initialized = true;
  } catch (e) {
    console.error("Failed to initialize Mixpanel: ", e);
  }
}

// Re-attempt init the moment a partner grants consent (e.g. via the banner),
// and tear down tracking immediately if they decline after previously granting.
onAnalyticsConsentChange((granted) => {
  if (granted) {
    initMixpanel();
    // Undoes any prior opt-out at the SDK level (persisted in Mixpanel's own
    // storage) - needed if this partner declined earlier in this browser and
    // is now accepting. A no-op if they'd never opted out.
    if (initialized) {
      mixpanel.opt_in_tracking();
    }
  } else if (initialized) {
    // opt_out_tracking() is the real SDK-level off switch: it persists its
    // own flag and makes the SDK itself refuse to send anything - including
    // the automatic track_pageview pageview - not just our trackEvent
    // wrapper's `initialized` check. reset() alone only clears identity
    // (distinct_id, super properties); it doesn't stop the SDK from tracking.
    mixpanel.opt_out_tracking();
    mixpanel.reset();
    initialized = false;
  }
});

// Attempt init on module load too, in case consent was already granted in a
// prior session - this is what makes init happen "on app boot" for returning,
// already-consented partners.
initMixpanel();

export const identifyUser = (user) => {
  if (!user || !initialized) return;

  try {
    mixpanel.identify(user.email);
    mixpanel.people.set({
      $name: `${user.first_name} ${user.last_name}`,
      $email: user.email,
    });
  } catch (e) {
    console.error("Failed to identify user in Mixpanel: ", e);
  }
};

export const trackEvent = (eventName, properties = {}, options = {}) => {
  if (!initialized) return;

  try {
    mixpanel.track(eventName, properties, options);
  } catch (e) {
    console.error(`Failed to track ${eventName} in Mixpanel: `, e);
  }
};

// For events fired immediately before window.location.href navigates away
// (e.g. login/logout redirects) - a normal XHR can be aborted mid-flight by
// the navigation, so these use navigator.sendBeacon instead.
export const trackEventBeforeNavigation = (eventName, properties = {}) => {
  trackEvent(eventName, properties, { transport: "sendBeacon" });
};

// Fires the pad_engagement_activity heartbeat. active_users_rolling_14d is
// intentionally not sent - there's no client-side source of truth for it,
// and it's better computed from Mixpanel's own rolling-unique-user reporting
// than faked here.
export const trackEngagementActivity = (activityType) => {
  trackEvent("pad_engagement_activity", {
    activity_type: activityType,
    num_actions_today: incrementAndGetDailyActionCount(activityType),
    session_duration_seconds: getSessionDurationSeconds(),
  });
};

export default mixpanel;
